import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuerySample } from '@store/helpers';
import {
  ConversationDTO,
  MessageDTO,
  toChatMessage,
  toChatMessages,
  toDriverConversation,
} from '@component/DriverChat/adapters';
import { DRIVER_CHAT_MESSAGES_PAGE_SIZE } from '@component/DriverChat/constants';
import {
  ChatMessage,
  ChatThreadData,
  DriverConversation,
} from '@component/DriverChat/types';

export type ConversationFilter = 'MINE' | 'ACTIVE' | 'TODAY';
const LIST_SIZE = 50;

export type ConversationCounts = {
  active: number;
  today: number;
  mine: number;
};

type SendMessageArgs = {
  conversationId: number;
  text: string;
  clientMessageId: string;
};

type ReadArgs = { conversationId: number; lastReadMessageId: number };

type ListArgs = { filter: ConversationFilter; search?: string };

type CountsArgs = { serviceDate: string; search?: string };

type OlderArgs = { conversationId: number; beforeMessageId: number };

const toThreadData = (response: {
  messages?: MessageDTO[];
  hasMore?: boolean;
}): ChatThreadData => ({
  messages: toChatMessages(response?.messages),
  hasMore: !!response?.hasMore,
});

const BASE = '/twilio/driver-chat';
const BASE_SOCKET = '/websocket/driver-chat';

export const driverChatApi = createApi({
  reducerPath: 'DriverChat',
  tagTypes: ['Conversations', 'ConversationCounts', 'Messages', 'Unread'],
  baseQuery: baseQuerySample,

  endpoints: (builder) => ({
    getDriverConversations: builder.query<DriverConversation[], ListArgs>({
      query: ({ filter, search }) => ({
        url: `${BASE}/conversations`,
        params: {
          filter,
          page: 0,
          size: LIST_SIZE,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      }),
      transformResponse: (response: { content?: ConversationDTO[] }) =>
        (response?.content ?? []).map(toDriverConversation),
      providesTags: ['Conversations'],
    }),

    getDriverConversationCounts: builder.query<ConversationCounts, CountsArgs>({
      query: ({ serviceDate, search }) => ({
        url: `${BASE}/conversations/counts`,
        params: {
          serviceDate,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      }),
      transformResponse: (response: Partial<ConversationCounts>) => ({
        active: response?.active ?? 0,
        today: response?.today ?? 0,
        mine: response?.mine ?? 0,
      }),
      providesTags: ['ConversationCounts'],
    }),

    openDriverConversation: builder.mutation<DriverConversation, number>({
      query: (rideSegmentId) => ({
        url: `${BASE}/conversations`,
        method: 'POST',
        body: { rideSegmentId },
      }),
      transformResponse: (response: ConversationDTO) =>
        toDriverConversation(response),
      invalidatesTags: ['Conversations', 'ConversationCounts'],
    }),

    getDriverConversation: builder.query<DriverConversation, number>({
      query: (conversationId) => ({
        url: `${BASE}/conversations/${conversationId}`,
      }),
      transformResponse: (response: ConversationDTO) =>
        toDriverConversation(response),
      providesTags: (_result, _error, id) => [{ type: 'Conversations', id }],
    }),

    getDriverChatMessages: builder.query<ChatThreadData, number>({
      query: (conversationId) => ({
        url: `${BASE}/conversations/${conversationId}/messages`,
        params: { limit: DRIVER_CHAT_MESSAGES_PAGE_SIZE },
      }),
      transformResponse: toThreadData,
      providesTags: (_result, _error, conversationId) => [
        { type: 'Messages', id: conversationId },
      ],
    }),

    loadOlderDriverChatMessages: builder.mutation<ChatThreadData, OlderArgs>({
      query: ({ conversationId, beforeMessageId }) => ({
        url: `${BASE}/conversations/${conversationId}/messages`,
        params: { beforeMessageId, limit: DRIVER_CHAT_MESSAGES_PAGE_SIZE },
      }),
      transformResponse: toThreadData,
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            driverChatApi.util.updateQueryData(
              'getDriverChatMessages',
              conversationId,
              (draft) => {
                const known = new Set(draft.messages.map((m) => m.id));
                draft.messages = [
                  ...data.messages.filter((m) => !known.has(m.id)),
                  ...draft.messages,
                ];
                draft.hasMore = data.hasMore;
              }
            )
          );
        } catch {
          // The button stays available; the next click retries.
        }
      },
    }),

    // Sent over REST (not STOMP) so every message is persisted and audited
    // server-side; clientMessageId makes retries idempotent (no second SMS).
    sendDriverChatMessage: builder.mutation<ChatMessage, SendMessageArgs>({
      query: ({ conversationId, text, clientMessageId }) => ({
        url: `${BASE}/conversations/${conversationId}/messages`,
        method: 'POST',
        body: { clientMessageId, message: text },
      }),
      transformResponse: (response: MessageDTO) => toChatMessage(response),
      async onQueryStarted(
        { conversationId, text, clientMessageId },
        { dispatch, queryFulfilled }
      ) {
        const patchMessage = (update: (message: ChatMessage) => void) =>
          dispatch(
            driverChatApi.util.updateQueryData(
              'getDriverChatMessages',
              conversationId,
              (draft) => {
                const message = draft.messages.find(
                  (m) => m.clientMessageId === clientMessageId
                );
                if (message) update(message);
              }
            )
          );

        dispatch(
          driverChatApi.util.updateQueryData(
            'getDriverChatMessages',
            conversationId,
            (draft) => {
              const existing = draft.messages.find(
                (m) => m.clientMessageId === clientMessageId
              );
              if (existing) {
                existing.status = 'sending';
                return;
              }
              draft.messages.push({
                id: clientMessageId,
                clientMessageId,
                conversationId,
                sender: 'DISPATCHER',
                text,
                createdAt: new Date().toISOString(),
                status: 'sending',
              });
            }
          )
        );

        try {
          const { data } = await queryFulfilled;
          patchMessage((message) => {
            Object.assign(message, data, { clientMessageId });
          });
          patchConversations(dispatch, (draft) => {
            const conversation = draft.find(
              (c) => c.conversationId === conversationId
            );
            if (conversation) {
              conversation.lastMessage = {
                sender: data.sender,
                text: data.text,
                createdAt: data.createdAt,
                status: data.status,
              };
            }
          });
        } catch (error) {
          patchMessage((message) => {
            message.status = 'failed';
          });
          // 409 CONVERSATION_NOT_ACTIVE: the ride ended, the thread is read-only now.
          if (
            (error as { error?: { status?: number } })?.error?.status === 409
          ) {
            patchConversations(dispatch, (draft) => {
              const conversation = draft.find(
                (c) => c.conversationId === conversationId
              );
              if (conversation) conversation.conversationStatus = 'COMPLETED';
            });
          }
        }
      },
    }),

    markDriverChatRead: builder.mutation<
      {
        conversationId: number;
        lastReadMessageId: number;
        unreadCount: number;
      },
      ReadArgs
    >({
      query: ({ conversationId, lastReadMessageId }) => ({
        url: `${BASE}/conversations/${conversationId}/read`,
        method: 'POST',
        body: { lastReadMessageId },
      }),
      async onQueryStarted(
        { conversationId },
        { dispatch, getState, queryFulfilled }
      ) {
        const patches = cachedListArgs(getState()).map((args) =>
          dispatch(
            driverChatApi.util.updateQueryData(
              'getDriverConversations',
              args,
              (draft) => {
                const conversation = draft.find(
                  (c) => c.conversationId === conversationId
                );
                if (conversation) conversation.unreadCount = 0;
              }
            )
          )
        );
        try {
          await queryFulfilled;
          dispatch(driverChatApi.util.invalidateTags(['Unread']));
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),

    getDriverChatUnreadCount: builder.query<number, void>({
      query: () => ({ url: `${BASE}/unread-count` }),
      transformResponse: (response: { count?: number }) => response?.count ?? 0,
      providesTags: ['Unread'],
    }),

    getDriverChatQuickReplies: builder.query<string[], void>({
      query: () => ({ url: `${BASE}/quick-replies` }),
    }),

    // Single-use, 60 s ticket: never cache, fetch a fresh one per connection.
    getDriverChatWsTicket: builder.query<
      { ticket: string; expiresAt: number },
      void
    >({
      // query: () => ({ url: `${BASE}/ws-ticket` }),
      query: () => ({ url: `${BASE_SOCKET}/ws-ticket` }),
      keepUnusedDataFor: 0,
    }),
  }),
});

/** Args of every cached list (any tab, any search term). */
export const cachedListArgs = (state: any): ListArgs[] =>
  Object.values<any>(state?.[driverChatApi.reducerPath]?.queries ?? {})
    .filter(
      (entry) =>
        entry?.endpointName === 'getDriverConversations' &&
        entry.status === 'fulfilled'
    )
    .map((entry) => entry.originalArgs as ListArgs);

/** Every conversation held by any cached list, first match wins. */
export const selectCachedConversation = (
  state: any,
  predicate: (conversation: DriverConversation) => boolean
): DriverConversation | undefined => {
  const queries = Object.values<any>(
    state?.[driverChatApi.reducerPath]?.queries ?? {}
  );
  for (const entry of queries) {
    if (entry?.endpointName === 'getDriverConversations') {
      const found = (entry.data as DriverConversation[] | undefined)?.find(
        predicate
      );
      if (found) return found;
    }
  }
  return undefined;
};

/** Patches every cached list variant; a missing cache entry is a no-op. */
export function patchConversations(
  dispatch: (action: any) => unknown,
  recipe: (draft: DriverConversation[]) => void
) {
  dispatch((innerDispatch: any, getState: () => unknown) =>
    cachedListArgs(getState()).forEach((args) =>
      innerDispatch(
        driverChatApi.util.updateQueryData(
          'getDriverConversations',
          args,
          recipe
        )
      )
    )
  );
}

export const {
  useGetDriverConversationsQuery,
  useGetDriverConversationCountsQuery,
  useOpenDriverConversationMutation,
  useGetDriverConversationQuery,
  useGetDriverChatMessagesQuery,
  useLoadOlderDriverChatMessagesMutation,
  useSendDriverChatMessageMutation,
  useMarkDriverChatReadMutation,
  useGetDriverChatUnreadCountQuery,
  useGetDriverChatQuickRepliesQuery,
} = driverChatApi;
